import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router";
import { supabase } from "../supabase";
import { useSitzung } from "../sitzung";
import { merkenSetzen } from "../speicher";
import { fehlertext } from "../fehler";
import { Feld } from "../ui/Feld";
import { Knopf } from "../ui/Knopf";
import { Wortmarke } from "../ui/Wortmarke";
import { Meldung } from "../ui/Leer";
import { istInstalliert, istIos } from "../push";
import s from "./seiten.module.css";

const NEU_SENDEN_NACH = 60;

/** Ansicht 15: E-Mail, dann Code mit 6 Ziffern in einem Feld. Nur für eingeladene Personen (shouldCreateUser: false). */
export function Anmelden() {
  const { sitzung } = useSitzung();
  const [schritt, setSchritt] = useState<"mail" | "code">("mail");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [merken, setMerken] = useState(true);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [warten, setWarten] = useState(0);

  useEffect(() => {
    if (warten <= 0) return;
    const t = window.setTimeout(() => setWarten((w) => w - 1), 1000);
    return () => window.clearTimeout(t);
  }, [warten]);

  if (sitzung.status === "angemeldet") return <Navigate to="/" replace />;

  async function codeSenden(e?: FormEvent) {
    e?.preventDefault();
    if (!supabase) return;
    const adresse = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adresse)) {
      setFehler("Bitte prüf die E-Mail-Adresse.");
      return;
    }
    setLaeuft(true);
    setFehler(null);
    const { error } = await supabase.auth.signInWithOtp({ email: adresse, options: { shouldCreateUser: false } });
    setLaeuft(false);
    // Unbekannte Adressen bekommen dieselbe Antwort wie bekannte: Die App verrät nicht, wer einen Zugang hat.
    const unbekannt = error && (error.code === "otp_disabled" || error.code === "signup_disabled" || /signups? not allowed/i.test(error.message));
    if (error && !unbekannt) {
      setFehler(fehlertext(error));
      return;
    }
    setEmail(adresse);
    setSchritt("code");
    setWarten(NEU_SENDEN_NACH);
  }

  async function codePruefen(e: FormEvent) {
    e.preventDefault();
    if (!supabase) return;
    const ziffern = code.replace(/\D/g, "");
    if (ziffern.length !== 6) {
      setFehler("Der Code hat 6 Ziffern.");
      return;
    }
    setLaeuft(true);
    setFehler(null);
    merkenSetzen(merken);
    const { error } = await supabase.auth.verifyOtp({ email, token: ziffern, type: "email" });
    setLaeuft(false);
    if (error) setFehler(fehlertext(error));
  }

  return (
    <div className={s.anmelden}>
      <div className={s.anmeldenBild}>
        <div>
          <h1>
            <Wortmarke /> App.
          </h1>
          <p>Aufgaben, Sitzungen und Redaktion an einem Ort. Für Vorstand, Mitglieder und Redaktion von obsn e.V.</p>
        </div>
        <p>app.obsn.de</p>
      </div>
      <main className={s.anmeldenForm}>
        <div className={s.anmeldenKarte}>
          {schritt === "mail" ? (
            <form onSubmit={codeSenden} className={s.stapel} noValidate>
              <h2>Anmelden</h2>
              <p>Gib deine E-Mail-Adresse ein. Du bekommst einen Code mit 6 Ziffern. Ein Passwort gibt es nicht.</p>
              {istIos() && !istInstalliert() ? (
                <p className={s.klein}>Auf dem iPhone: Wenn du Push willst, füge die App zuerst über „Teilen“ zum Home-Bildschirm hinzu und melde dich dann dort an.</p>
              ) : null}
              <Feld label="E-Mail" type="email" name="email" autoComplete="email" inputMode="email" required value={email} onChange={(e) => setEmail(e.target.value)} fehler={fehler} />
              <Knopf art="primaer" type="submit" disabled={laeuft}>
                {laeuft ? "Code wird gesendet …" : "Code senden"}
              </Knopf>
              <p className={s.klein}>Nur für freigeschaltete Personen. Noch keinen Zugang? Frag im Vorstand nach.</p>
            </form>
          ) : (
            <form onSubmit={codePruefen} className={s.stapel} noValidate>
              <h2>Code eingeben</h2>
              <p>
                Wenn {email} freigeschaltet ist, kommt jetzt ein Code. Er gilt 10 Minuten.
              </p>
              <Feld
                label="Code mit 6 Ziffern"
                name="code"
                eingabeKlasse={s.code}
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={7}
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
                fehler={fehler}
              />
              <label className={s.zeile} style={{ minHeight: 44 }}>
                <input type="checkbox" checked={merken} onChange={(e) => setMerken(e.target.checked)} style={{ width: 22, height: 22 }} />
                Auf diesem Gerät 30 Tage angemeldet bleiben
              </label>
              <Knopf art="primaer" type="submit" disabled={laeuft}>
                {laeuft ? "Wird geprüft …" : "Anmelden"}
              </Knopf>
              <Meldung>
                Keinen Code bekommen? Schau im Spam-Ordner nach und prüf die Adresse.{" "}
                <button type="button" className={s.textknopf} disabled={warten > 0 || laeuft} onClick={() => void codeSenden()}>
                  {warten > 0 ? `Neu senden in ${warten} s` : "Neu senden"}
                </button>
              </Meldung>
              <button
                type="button"
                className={s.textknopf}
                onClick={() => {
                  setSchritt("mail");
                  setCode("");
                  setFehler(null);
                }}
              >
                Andere E-Mail
              </button>
            </form>
          )}
          <p className={s.klein}>
            <Link to="/datenschutz">Datenschutz</Link>
          </p>
        </div>
      </main>
    </div>
  );
}
